import {AppProps} from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import Router, {useRouter} from 'next/router'
import {useEffect, useMemo, useState} from 'react'

import {DefignProvider, modal} from '@defign/ui'
import {generateUserAgentValues} from '@financial/app-js-interfaces'
import {UserAgentProvider} from '@financial/common-contexts'
import {initCommonLogsEnv, LogEnv, addGlobalAUHandler} from '@financial/common-logs'
import {SimplepayLayerProvider} from '@simplepay/account-packages'
import {HydrationBoundary, QueryClient, QueryClientProvider} from '@tanstack/react-query'

import {InflowAuSender} from '$components/bank/common/InflowAuSender'
import MydataOptionalAgreementLayer from '$components/common/MydataOptionalAgreementLayer'
import ErrorBoundary from '$components/error-boundary'
import AppLinkerProvider from '$components/insurance/common/AppLinkerProvider'
import {FE_CDN_ORIGIN, META_TITLE} from '$config'
import {INTERNAL_ROUTES} from '$constants/internal-routes'
import {MemberInfoProvider} from '$contexts/member'
import MssChecker from '$contexts/mss'
import {PopupProvider} from '$contexts/popup'
import {UpdaterProvider} from '$contexts/updater/context'
import {CardListMonthProvider} from '$hooks/useCardListMonth'
import {isRealProfile, isStageProfile} from '$share/utils/env'
import {MemberInfoWithStatus, UserInfo} from '$types/member'
import {isMatchingRoute} from '$utils/common'
import {ServerDate} from '$utils/date'
import {createInsuranceQueryClient} from '$utils/insurance/insuranceQueryClientConfig'
import {setMyDataAUPage} from '$utils/log'
import queryClientConfig from '$utils/queryClientConfig'

import '$scss/npay_global.scss'
import '$scss/App.scss'
import '@defign/ui/styles/defign.css'
import '@mydata/components/ConsentLayer/index.css'
import '@mydata/components/OptionalAgreementLayer/index.css'
import '@mydata/components/InspectionTimeToast/index.css'

const DynamicDevTools = dynamic(() => import('$components/playground/dev-tools'), {ssr: false})

interface DefaultProps {
    userAgent: string
    memberInfo: MemberInfoWithStatus
    serverDateTime: number
    userInfo?: UserInfo
    dehydratedState?: unknown
    isPinchToZoomEnablePage?: boolean
    isSuccessLogin?: boolean
}

function MyassetApp({Component, pageProps}: AppProps<DefaultProps>) {
    const router = useRouter()

    const {pathname} = router

    const isInsurancePage = pathname.startsWith('/insurance')

    const {
        userAgent,
        memberInfo,
        isSuccessLogin,
        serverDateTime,
        dehydratedState = {},
        isPinchToZoomEnablePage,
        ...restPageProps
    } = pageProps

    const [queryClient] = useState<QueryClient>(() =>
        isInsurancePage ? createInsuranceQueryClient() : new QueryClient(queryClientConfig()),
    )

    const appInfo = useMemo(() => {
        const {isNaverApp, isPayApp} = generateUserAgentValues(userAgent || '')

        return {
            isNaverApp,
            isPayApp,
        }
    }, [userAgent])

    useEffect(() => {
        /** common-logs 환경설정 */
        initCommonLogsEnv(isRealProfile || isStageProfile ? LogEnv.real : LogEnv.dev)

        /** FDC AU 증적 */
        addGlobalAUHandler()
        setMyDataAUPage(window.location.pathname)

        const {isPayApp} = generateUserAgentValues(window.navigator.userAgent)

        /** NLog pageView 적용 */
        window.g_sti = isPayApp ? 'm_payapp' : ''
        window.ntm?.push({event: 'pageview'})

        window.g_ssc = 'Mfams.mydata'

        Router.events.on('routeChangeStart', (url: string) => {
            modal.clear()
            setMyDataAUPage(url)
        })

        Router.events.on('hashChangeStart', (url: string) => {
            const hash = url.split('#')[1]

            if (!hash) {
                modal.clear()
            }
        })
    }, [])

    if (serverDateTime) {
        ServerDate(serverDateTime)
    }

    const hasCustomOgTag = isMatchingRoute(INTERNAL_ROUTES['INSURANCE_CONTENTS_[ID]'], pathname)

    return (
        <>
            <Head>
                <title>{META_TITLE}</title>
                <meta
                    name="viewport"
                    content={
                        isPinchToZoomEnablePage
                            ? 'width=device-width,initial-scale=1.0'
                            : 'width=device-width,initial-scale=1.0,maximum-scale=1.0,minimum-scale=1.0,user-scalable=no'
                    }
                />
                {hasCustomOgTag ? null : (
                    <>
                        <meta property="og:title" content={META_TITLE} />
                        <meta
                            property="og:image"
                            content={`${FE_CDN_ORIGIN}/static/public/image/npay_og_260x260.png`}
                        />
                        <meta id="metaOgUrl" property="og:url" content="https://new-m.pay.naver.com" />
                        <meta property="og:description" content="네이버 ID로 간편구매, 네이버페이" />
                    </>
                )}
            </Head>
            <UserAgentProvider userAgent={userAgent || ''}>
                <MemberInfoProvider memberInfo={memberInfo}>
                    <UpdaterProvider userInfo={restPageProps.userInfo}>
                        <ErrorBoundary>
                            <SimplepayLayerProvider
                                appInfo={appInfo}
                                memberInfo={
                                    memberInfo?.status === 'success'
                                        ? memberInfo
                                        : {birthday: '', name: '', phoneNo: '', age: 0}
                                }
                            >
                                <QueryClientProvider client={queryClient}>
                                    <HydrationBoundary state={dehydratedState}>
                                        <DefignProvider>
                                            <PopupProvider>
                                                <MssChecker>
                                                    <CardListMonthProvider>
                                                        <AppLinkerProvider>
                                                            <Component {...restPageProps} />
                                                        </AppLinkerProvider>
                                                        <MydataOptionalAgreementLayer isSuccessLogin={isSuccessLogin} />
                                                    </CardListMonthProvider>
                                                </MssChecker>
                                            </PopupProvider>
                                        </DefignProvider>
                                    </HydrationBoundary>
                                </QueryClientProvider>
                            </SimplepayLayerProvider>
                        </ErrorBoundary>
                    </UpdaterProvider>
                </MemberInfoProvider>
            </UserAgentProvider>
            <DynamicDevTools />
            <InflowAuSender />
        </>
    )
}

export default MyassetApp
